import React from 'react';
import PropTypes from 'prop-types';

import i18next from 'i18next';
import { useTranslation } from 'react-i18next';

import useAppContext from '../../../context/useAppContext';
import { ToastContext, Loader } from '@weezevent/nacre';

import AttendeeComment from '../../../models/attendeeComment';

import { Form, Button, Segment, Feed, Input, Divider } from 'semantic-ui-react';

const SECONDS = 1000;
const MINUTES = SECONDS * 60;
const HOURS = MINUTES * 60;
const DAYS = HOURS * 24;

const TIME_VALUES = [
    {
        value: 'day',
        comparator: DAYS
    },
    {
        value: 'hour',
        comparator: HOURS
    },
    {
        value: 'minute',
        comparator: MINUTES
    },
    {
        value: 'second',
        comparator: SECONDS
    }
];

const get_time_delta = date => {
    let delta = Date.now() - date;
    let size = TIME_VALUES[0];
    for (let i = 0; i < TIME_VALUES.length; i++) {
        if (delta < size.comparator) {
            size = TIME_VALUES[i - 1] ? TIME_VALUES[i - 1] : TIME_VALUES[i];
        }
    }

    delta = -parseInt(delta / size.comparator);
    return { delta, size };
};

function Comment({ comment }) {
    const { t } = useTranslation();
    const formatter = new window.Intl.RelativeTimeFormat(i18next.language);
    let date = new Date(comment.created).getTime();
    let { delta, size } = get_time_delta(date);

    let user = comment.user;
    let userLabel = `${user.first_name} ${user.last_name} (${user.email})`;

    return (
        <React.Fragment>
            <Feed.Event>
                <Feed.Content>
                    <Feed.Date>{`${t('ticket.attendee.comments.created')} ${formatter.format(delta, size.value)}`}</Feed.Date>
                    <Feed.Summary>{userLabel}</Feed.Summary>
                    <Feed.Extra text>{comment.comment}</Feed.Extra>
                </Feed.Content>
            </Feed.Event>
            <Divider />
        </React.Fragment>
    );
}

Comment.propTypes = {
    comment: PropTypes.object.isRequired
};

function NewComment({ organizationId, attendeeId, eventId, onSave = () => {} }) {
    const { t } = useTranslation();
    let [comment, setComment] = React.useState('');
    const toast = React.useContext(ToastContext);

    const save = React.useCallback(() => {
        let apiComment = new AttendeeComment();
        apiComment.comment = comment;

        apiComment
            .save({ organizationId, attendeeId, eventId })
            .then(() => {
                toast.success(t('ticket.attendee.comments.saveSucess'));
                setComment('');
                onSave();
            })
            .catch(e => {
                toast.error(t('ticket.attendee.comments.saveError'));
            });
    }, [comment]);

    const change = React.useCallback(
        ({ keyCode, target: { value } }) => {
            setComment(value);
        },
        [save]
    );

    return (
        <Form onSubmit={save}>
            <Form.Field>
                <label>{t('ticket.attendee.comments.newComment')}</label>
                <Input fluid value={comment} type="text" placeholder={t('ticket.attendee.comments.newCommentPlaceholder')} onChange={change} />
                <Button disabled={!comment || comment === ''} primary type="submit" content={t('ticket.common.action.save')} />
            </Form.Field>
        </Form>
    );
}

NewComment.propTypes = {
    organizationId: PropTypes.number.isRequired,
    attendeeId: PropTypes.number.isRequired,
    eventId: PropTypes.number.isRequired,
    onSave: PropTypes.func
};

export default function AttendeeComments({ attendeeId }) {
    const { t } = useTranslation();
    const [{ organization, event }] = useAppContext();
    const [comments, setComments] = React.useState('');
    const toast = React.useContext(ToastContext);

    const load = React.useCallback(() => {
        AttendeeComment.getAll({ eventId: event.id, organizationId: organization.id, attendeeId })
            .then(apiComments => {
                setComments(apiComments);
            })
            .catch(e => {
                toast.error(t('ticket.attendee.comments.retrievalError'));
                setComments([]);
            });
    }, [setComments]);

    // First load
    React.useEffect(load, []);

    if (!comments) {
        return (
            <Segment basic>
                <Loader />
            </Segment>
        );
    }

    let feedComments = comments.map((comment, index) => <Comment key={index} comment={comment} />);

    if (!comments.length) {
        return (
            <div>
                <h5>{t('ticket.attendee.comments.noComments')}</h5>
                <NewComment organizationId={organization.id} eventId={event.id} attendeeId={attendeeId} onSave={load} />
            </div>
        );
    }

    return (
        <Feed>
            {feedComments}
            <NewComment organizationId={organization.id} eventId={event.id} attendeeId={attendeeId} onSave={load} />
        </Feed>
    );
}

AttendeeComments.propTypes = {
    organization: PropTypes.object.isRequired,
    attendeeId: PropTypes.number.isRequired,
    event: PropTypes.object.isRequired
};
